/* A component that renders the form template */
import React from "react"
import fieldMap from "../../DynamicForm/Fields/FieldMap"
import FieldTypes from "../../Types/FieldTypes"
import DynamicFormPaletteTypes from "src/store/DyanmicFormPalette/types"
import schemaBoiler from "../../Utils/SchemaBoiler"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import TrashCan from "../../Utils/TrashCan/TrashCan"
import paletteEngine from "../../Utils/paletteEngine"
import "./DynamicFormTemplate.css"

interface Props {
    manageParentState: Function
    handleChange: Function
    template: Array<Array<FieldTypes>>
    target: { row: number, col: number, key: string }
    highlightTarget: { row: number, col: number }
    mouseInFieldManager: boolean
    addOrRemoveColumn: Function
    addOrRemoveRow: Function
    hints: boolean
    forceMobile: boolean
    palette: Array<DynamicFormPaletteTypes>
}

const DynamicFormTemplate = (props: Props) => {

    let template = props.template
    const palette = props.palette
    const empty = template.length === 0
    const columnLimit = 4
    const mouseInFieldManager = props.mouseInFieldManager

    /**
     * "addEmptyColumn" is a function that takes a row number as an argument and adds a new column to the
     * row.
     * @param {number} row - number - the row number of the table
     * @returns the template array.
     */
    const addEmptyColumn = (row: number) => {
        template[row].push(schemaBoiler)
        template = template.filter(function (element: any) { return element !== undefined })
        props.manageParentState([{ key: "template", value: template }], true)
    }

    const handleAddRow = () => {
        props.addOrRemoveRow(-1)
        let elem = document.getElementById("dynamic-form-template-add-row")
        setTimeout(() => {
            //@ts-ignore
            elem.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
        }, 100)
    }

    /**
     * Reorder the template array by splicing the array at the new index and inserting the element at the
     * old index.
     * @param direction - { rNew: number, rOld: number }
     */
    const reorder = (direction: { rNew: number, rOld: number }) => {
        props.template.splice(direction.rNew, 0, props.template.splice(direction.rOld, 1)[0])
        props.manageParentState([{ key: "template", value: props.template }], true)
    }

    const DynamicFormTemplateToolTip = (text: string) => (
        <Tooltip id={`dynamic-form-tool-tip-hide-${!props.hints}`}>
            {text}
        </Tooltip>
    )

    return (
        <div className={`DynamicFormTemplate ${empty ? "DynamicFormTemplateEmpty" : ""} DynamicFormTemplate${props.forceMobile ? "Mobile" : "Desktop"}`}>
            {empty && <div className="DynamicFormTemplateEmptyHint">
                <span className="material-icons">dynamic_form</span>
                <p>Add a row to get started...</p>
            </div>}
            {!empty && template.map((row, i: number) =>
                <div className="DynamicFormTemplateRow" id={`dynamic-form-template-row-${i}`} key={`dynamic-form-template-row-${i} `}>
                    <div id={`dynamic-form-template-row-left-${i}`} className="DynamicFormTemplateRowLeft">
                        <OverlayTrigger
                            placement="left"
                            overlay={DynamicFormTemplateToolTip(row.length < columnLimit ? `Add empty column to row ${i + 1}` : `Max columns are ${columnLimit}`)}>
                            <div className={`DynamicFormTemplateRowAction DynamicFormTemplateRowActionAddColumn ${row.length === columnLimit ? "DynamicFormTemplateRowActionAddColumnDisbaled" : ""} ${mouseInFieldManager ? "DynamicFormTemplateRowActionHide" : "DynamicFormTemplateRowActionShow"}`}
                                onClick={row.length < columnLimit ? () => addEmptyColumn(i) : () => null}>
                                <span className="material-icons">add</span>
                                <p>c</p>
                            </div>
                        </OverlayTrigger>
                        <OverlayTrigger placement="right" overlay={DynamicFormTemplateToolTip(`Delete row ${i + 1}`)}>
                            <div className={`DynamicFormTemplateRowAction DynamicFormTemplateRowActionDeleteRow ${mouseInFieldManager ? "DynamicFormTemplateRowActionHide" : "DynamicFormTemplateRowActionShow"}`}
                                onClick={() => props.addOrRemoveRow(i)}>
                                <TrashCan color="#dc3545" />
                                <p>r</p>
                            </div>
                        </OverlayTrigger>
                        {mouseInFieldManager && props.highlightTarget.row === i && <span id="dynamic-form-template-row-hint" className={`material-icons ${props.highlightTarget.col > -1 ? "DynamicFormTemplateRowHintGreen" : "DynamicFormTemplateRowHintBlue"}`}>arrow_forward</span>}
                    </div>
                    <div className={`DynamicFormTemplateRowMiddle ${i === props.highlightTarget.row ? "DynamicFormTemplateRowMiddleTargeted" : "DynamicFormTemplateRowMiddleDormant"} ${i === 0 ? "DynamicFormTemplateRowMiddleFirst" : ""}`}
                        style={{ backgroundColor: `${paletteEngine.getPaletteColor("formBackgroundColor")}` }}>
                        {row.map((field, j: number) =>
                            <div className={`DynamicFormTemplateColumn ${(i === props.highlightTarget.row && j === props.highlightTarget.col) ? "DynamicFormTemplateColumnTargeted" : "DynamicFormTemplateColumnDormant"}`}
                                key={`dynamic-form-template-column-${j}`}
                                style={{ maxWidth: !props.forceMobile ? `calc(${100 / row.length}% - 5px)` : "100%" }}>
                                {fieldMap.buildField(field.Type, i, j, template, palette, props.handleChange, props.manageParentState, props.addOrRemoveColumn, true)}
                            </div>)}
                    </div>
                    <div id={`dynamic-form-template-row-right-${i}`} className="DynamicFormTemplateRowRight">
                        <div className="DynamicFormTemplateRowRightReorder">
                            <OverlayTrigger placement="left" overlay={DynamicFormTemplateToolTip(`Move r${i + 1} to r${i + 2}`)}>
                                <div className={`DynamicFormTemplateRowRightReorderBtn DynamicFormTemplateRowRightReorderBtn${i < template.length - 1 ? "Enabled" : "Disabled"} ${mouseInFieldManager ? "DynamicFormTemplateRowRightReorderBtnHide" : "DynamicFormTemplateRowRightReorderBtnShow"}`}
                                    onClick={() => reorder({ rNew: i + 1, rOld: i })}>
                                    <span className="material-icons">south</span>
                                </div>
                            </OverlayTrigger>
                            <OverlayTrigger placement="right" overlay={DynamicFormTemplateToolTip(`Move r${i + 1} to r${i}`)}>
                                <div className={`DynamicFormTemplateRowRightReorderBtn DynamicFormTemplateRowRightReorderBtn${i > 0 ? "Enabled" : "Disabled"} ${mouseInFieldManager ? "DynamicFormTemplateRowRightReorderBtnHide" : "DynamicFormTemplateRowRightReorderBtnShow"}`}
                                    onClick={() => reorder({ rNew: i - 1, rOld: i })}>
                                    <span className="material-icons">north</span>
                                </div>
                            </OverlayTrigger>
                        </div>
                    </div>
                    <div className="DynamicFormTemplateRowRightRowNumber">
                        <p>{`r${i + 1}`}</p>
                    </div>
                </div>)}
            <div id="dynamic-form-template-add-row" className="DynamicFormTemplateRow">
                <div className="DynamicFormTemplateRowLeft"></div>
                <div className="DynamicFormTemplateRowMiddle DynamicFormTemplateRowAddRow" onClick={() => handleAddRow()}>
                    <p>Add row</p>
                    <span className="material-icons">add</span>
                </div>
                <div className="DynamicFormTemplateRowRight"></div>
            </div>
        </div>
    )
}

export default DynamicFormTemplate